import type { ElementType, ReactNode } from "react";

export interface SummaryCardProps {
  title?: string;
  description?: ReactNode;
  /** Independent header action, without making the card clickable. */
  headerAction?: ReactNode;
  /**
   * Makes the whole card a link to this destination. Omit it while the
   * destination does not exist; the card then renders as a plain section.
   */
  href?: string;
  /** Call to action shown on a linked card, such as "Open the map". */
  cta?: string;
  /** Where a linked card's call to action sits. */
  ctaPlacement?: "bottom" | "header";
  /**
   * Root element for a linked card. Pass a router `Link` (next/link or
   * react-router) to keep client-side navigation; `href` is forwarded to it.
   */
  linkAs?: ElementType;
  /** Content under the title, such as a figure or a chart. */
  children?: ReactNode;
  className?: string;
}

const BASE =
  "flex flex-col gap-5 rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) p-6";

function Cta({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-[12.5px] font-medium text-(--fg-secondary) transition-colors group-hover:text-(--fg-primary)">
      {label}
      <span
        aria-hidden={true}
        className="transition-transform group-hover:translate-x-0.5"
      >
        →
      </span>
    </span>
  );
}

/**
 * A bordered card for a landing-page summary. Linked, the whole card is the
 * click target and the call to action follows the content; unlinked, it is a
 * plain section.
 */
export function SummaryCard({
  title,
  description,
  headerAction,
  href,
  cta,
  ctaPlacement = "bottom",
  linkAs,
  children,
  className = "",
}: SummaryCardProps) {
  const showCta = Boolean(href && cta);
  const headerCta = showCta && ctaPlacement === "header";

  const header =
    title || description || headerCta || headerAction ? (
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex max-w-2xl flex-col gap-1.5">
          {/* tokens.css styles bare headings and paragraphs outside any cascade
              layer, so their typography and color need the `!` modifier. */}
          {title && (
            <h2 className="font-semibold! text-[17px]! leading-snug! tracking-tight! text-(--fg-primary)">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-[13px] leading-relaxed text-(--fg-secondary)!">
              {description}
            </p>
          )}
        </div>
        {headerAction ?? (headerCta && cta && <Cta label={cta} />)}
      </div>
    ) : null;

  const body = (
    <>
      {header}
      {children !== undefined && (
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      )}
      {showCta && !headerCta && cta && (
        <div className="mt-auto">
          <Cta label={cta} />
        </div>
      )}
    </>
  );

  if (href) {
    const Root = linkAs ?? "a";
    return (
      <Root
        href={href}
        className={`group ${BASE} text-(--fg-primary)! no-underline! transition-colors hover:border-(--border-strong) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary) ${className}`}
      >
        {body}
      </Root>
    );
  }
  return <section className={`${BASE} ${className}`}>{body}</section>;
}
